import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'

import Text from '../../ui/Text'
import findChargingConnector from '../findChargingConnector'
import findSchukoConnector from '../findSchukoConnector'
import { getConfigValue } from '../get-config-value'
import { remoteStartIsAvailable, remoteStopIsAvailable } from '../get-connector-state'
import isSchukoEnabled from '../isSchukoEnabled'
import { IConfigKey } from '../loadOcppConfigKeys'
import { KEY_FREE_CHARGING } from '../ocpp-aura-connector-control'
import { RemoteControlRow } from '../remote-control-row'
import { RemoteControlWidget } from '../remote-control-widget'
import { RemoteOcppDimmerControl } from '../remote-ocpp-dimmer-control'
import setOcppConfigValue from '../setOcppConfigValue'
import {
    ChargePoint,
    useChargePointOverrideScheduleForOngoingChargingSessionMutation,
    useChargePointRemoteStartMutation,
    useChargePointRemoteStopMutation,
    useOcppCommandUpdateConfigKeyMutation,
    useGreenChargingChargePointStatusQuery,
    useSmartChargingScheduleGetByChargePointQuery,
    useChargePointUpdateMutation,
    OnOffMode,
} from '../../common/redux/api/web-api'
import { Toggle } from '../../common/components/Toggle'
import { SelectRfidTagDialog } from '../../common/dialogs/select-rfid-tag-dialog'
import { isAdminDomain, isUserappDomain } from '../currentDomain'
import { onGoingTransactionStates } from '../charge-point-dawn-control-widget'
import { showErrorMessagesForIgnoreSchedule } from '../../common/utils/chargePointUtils'
import useText from '../../common/hooks/useText'
import { useToast } from '../../common/uikit/ToastProvider'
import { CHARGE_POINT_REFRESH_TIMEOUT } from '../../common/constants/ChargePointConstants'
import { SelectButton } from '../../common/uikit/SelectButton'

const KEY_DOWN_LIGHT = 'Downlight'

enum LightState {
    ON = '1',
    OFF = '0',
}

enum NfcLockState {
    ON = 'false',
    OFF = 'true',
}

export type ChargePointOcppHaloControlWidgetProps = {
    isReadOnly?: boolean
    chargePoint: ChargePoint
}

export function ChargePointOcppHaloControlWidget({
    isReadOnly = false,
    chargePoint,
}: ChargePointOcppHaloControlWidgetProps): React.ReactElement {
    const text = useText()
    const [configKeys, setConfigKeys] = useState<IConfigKey[]>(() => (chargePoint.ocppConfigKeys as IConfigKey[]) ?? [])
    const [chargingConnector, setChargingConnector] = useState(findChargingConnector(chargePoint.connectors ?? []))
    const [socketConnector, setSocketConnector] = useState(findSchukoConnector(chargePoint.connectors ?? []))
    const toast = useToast()

    const { data: schedules } = useSmartChargingScheduleGetByChargePointQuery(
        { chargePointId: chargePoint.id },
        { pollingInterval: CHARGE_POINT_REFRESH_TIMEOUT }
    )

    const connectorHasSchedule = (connectorId: number) => {
        return schedules?.some((schedule) => schedule.connectorId === connectorId)
    }

    const [updateChargePoint] = useChargePointUpdateMutation()

    const [saveConfigKey] = useOcppCommandUpdateConfigKeyMutation()
    const [remoteStart] = useChargePointRemoteStartMutation()
    const [remoteStop] = useChargePointRemoteStopMutation()
    const [overrideSchedule] = useChargePointOverrideScheduleForOngoingChargingSessionMutation()

    const [selectRfidDialogIsOpen, setSelectRfidDialogIsOpen] = useState(false)
    const isOnline = chargePoint.chargePointStatus !== 'Offline'
    const isLightOn = getConfigValue(KEY_DOWN_LIGHT, configKeys) === LightState.ON
    const isNfcLockOn = getConfigValue(KEY_FREE_CHARGING, configKeys) === NfcLockState.ON
    const { data: chargePointStatus } = useGreenChargingChargePointStatusQuery({ chargePointId: chargePoint.id })

    const remoteStartIsVisible = useMemo(() => remoteStartIsAvailable(chargingConnector), [chargingConnector])
    const remoteStopIsVisible = useMemo(() => remoteStopIsAvailable(chargingConnector), [chargingConnector])

    useEffect(() => {
        setConfigKeys(chargePoint.ocppConfigKeys)
        setChargingConnector(findChargingConnector(chargePoint.connectors ?? []))
        setSocketConnector(findSchukoConnector(chargePoint.connectors ?? []))
    }, [chargePoint])

    const toggleOcppConfigValue = async (key: string, value: string) => {
        const updatedConfigKeys = setOcppConfigValue(key, value, configKeys)
        if (!updatedConfigKeys) return
        await saveConfigKey({
            chargePointId: chargePoint.id,
            keyValue: updatedConfigKeys.find((configKeys) => configKeys.key === key),
        })
            .unwrap()
            .then(() => setConfigKeys(updatedConfigKeys))
            .catch(() => toast.notifyError(text('msgRemoteControlCommandFailed')))
    }

    const handleRemoteStart = async (rfidTag?: string) => {
        await remoteStart({
            chargePointId: chargePoint.id,
            connectorId: 1, // Halo has only one connector
            rfidTag,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgRemoteStartSuccess'))
            })
            .catch(() => {
                toast.notifyError(text(isAdminDomain ? 'msgRemoteStartAdminFailed' : 'msgRemoteStartFailed'))
            })
    }

    const handleRemoteControl = async () => {
        if (remoteStopIsVisible) {
            await remoteStop({
                chargePointId: chargePoint.id,
                connectorId: 1, //Halo has only one connector
            })
                .unwrap()
                .then(() => {
                    toast.notifySuccess(text('msgRemoteStopSuccess'))
                })
                .catch(() => {
                    toast.notifyError(text(isAdminDomain ? 'msgRemoteStopAdminFailed' : 'msgRemoteStopAdminFailed'))
                })
        } else {
            if (isNfcLockOn) {
                setSelectRfidDialogIsOpen(true)
            } else if (chargingConnector?.defaultNfcTagId) {
                handleRemoteStart(chargingConnector?.defaultNfcTagId)
            } else {
                toast.notifyError(text('msgRemoteStartFailed'))
            }
        }
    }

    const handleOverrideSchedule = async () => {
        await overrideSchedule({
            chargePointId: chargePoint.id,
            connectorId: 1,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgIgnoreScheduleSuccess'))
            })
            .catch((error) => {
                toast.notifyError(showErrorMessagesForIgnoreSchedule(error.data.error))
            })
    }
    const showStartStopChargingButton = !isUserappDomain && isOnline && (remoteStartIsVisible || remoteStopIsVisible)
    const showIgnoreScheduleButton =
        !isUserappDomain && isOnline && remoteStopIsVisible && chargingConnector?.mode == 'Schedule'

    const handleConnectorStateChange = (connectorId: number) => async (state: OnOffMode) => {
        if (state === 'Schedule' && !connectorHasSchedule(connectorId)) {
            return confirmDialog({
                header: text('msgNoSchedulesHeader'),
                message: text('msgNoSchedulesText'),
                acceptClassName: 'hidden',
                rejectLabel: text('btnClose'),
            })
        }

        const connector = connectorId === 1 ? chargingConnector : socketConnector
        const updatedConnector = { ...connector, mode: state }

        // Optimistically update the UI immediately
        if (connectorId === 1) {
            setChargingConnector(updatedConnector)
        } else if (connectorId === 2) {
            setSocketConnector(updatedConnector)
        }

        const updatedChargingConnector = connectorId === 1 ? updatedConnector : chargingConnector
        const updatedSocketConnector = connectorId === 2 ? updatedConnector : socketConnector

        const updatedConnectors: typeof chargePoint.connectors = []
        if (updatedChargingConnector) {
            updatedConnectors.push(updatedChargingConnector)
        }
        if (updatedSocketConnector) {
            updatedConnectors.push(updatedSocketConnector)
        }

        try {
            await updateChargePoint({
                chargePoint: {
                    ...chargePoint,
                    connectors: updatedConnectors,
                },
            }).unwrap()
        } catch (error) {
            // Revert the optimistic update on error
            const originalConnector =
                connectorId === 1
                    ? findChargingConnector(chargePoint.connectors ?? [])
                    : findSchukoConnector(chargePoint.connectors ?? [])

            if (connectorId === 1) {
                setChargingConnector(originalConnector)
            } else if (connectorId === 2) {
                setSocketConnector(originalConnector)
            }
            toast.notifyError(text('msgFailedToUpdateAvailability'))
        }
    }

    return (
        <RemoteControlWidget
            greenChargingStatus={chargePointStatus?.greenChargingStatus}
            isReadOnly={isReadOnly}
            chargePoint={chargePoint}
            title={'lblHaloControlWidget'}
        >
            {chargingConnector && (
                <RemoteControlRow
                    title={<div data-testid={`chargerText-${chargePoint.id}`}>{text('lblCharger')}</div>}
                    button={
                        <SelectButton<OnOffMode>
                            id={`charger-mode-${chargePoint.id}`}
                            ariaLabel={text('lblCharger')}
                            options={[
                                { label: text('lblOff'), value: 'Off' },
                                { label: text('lblConnectorSchedule'), value: 'Schedule' },
                                { label: text('lblConnectorAlwaysOn'), value: 'On' },
                            ]}
                            value={chargingConnector?.mode}
                            onChange={handleConnectorStateChange(1)}
                            disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                            className="w-20rem max-w-full"
                            data-testid={`charger-${chargePoint.id}`}
                        />
                    }
                    remoteChargingControl={
                        <div className="flex gap-2 flex-column sm:flex-row">
                            {showStartStopChargingButton && (
                                <Button
                                    tooltip={
                                        remoteStartIsVisible ? text('lblStartChargingInfo') : text('lblStopSession')
                                    }
                                    size="small"
                                    data-testid="remote-control-button"
                                    onClick={handleRemoteControl}
                                    label={text(remoteStopIsVisible ? 'lblStop' : 'lblStart')}
                                    outlined
                                    severity="info"
                                    icon={remoteStopIsVisible ? 'pi pi-stop-circle' : 'pi pi-play-circle'}
                                />
                            )}
                            {showIgnoreScheduleButton && (
                                <Button
                                    tooltip={text('lblIgnoreSchedule')}
                                    style={{ padding: '6px' }}
                                    data-testid="remote-control-button-ignoreSchedule"
                                    onClick={handleOverrideSchedule}
                                    outlined
                                    severity="info"
                                    icon="pi pi-calendar-times"
                                />
                            )}
                        </div>
                    }
                />
            )}

            {isSchukoEnabled(chargePoint) && socketConnector && (
                <RemoteControlRow
                    title={
                        <div data-testid={`socketText-${chargePoint.id}`}>
                            <Text code="lblSocket" />
                        </div>
                    }
                    button={
                        <SelectButton<OnOffMode>
                            id={`socket-mode-${chargePoint.id}`}
                            ariaLabel={text('lblSocket')}
                            options={[
                                { label: text('lblOff'), value: 'Off' },
                                { label: text('lblConnectorSchedule'), value: 'Schedule' },
                                { label: text('btnOn'), value: 'On' },
                            ]}
                            value={socketConnector.mode}
                            onChange={handleConnectorStateChange(2)}
                            disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                            className="w-20rem max-w-full"
                            data-testid={`socket-${chargePoint.id}`}
                        />
                    }
                />
            )}
            <RemoteControlRow
                title={
                    <div data-testid={`lightText-${chargePoint.id}`}>
                        <Text code="lblLight" data-testid={`lightText-${chargePoint.id}`} />
                    </div>
                }
                button={
                    <Toggle
                        toggleFn={() =>
                            toggleOcppConfigValue(KEY_DOWN_LIGHT, isLightOn ? LightState.OFF : LightState.ON)
                        }
                        isToggled={isLightOn}
                        disabled={isReadOnly || !isOnline || chargePoint?.ocppEnabled}
                        data-testid={`light-${chargePoint.id}`}
                    />
                }
            />
            <RemoteOcppDimmerControl chargePoint={chargePoint} isReadOnly={isReadOnly} />
            <RemoteControlRow
                title={
                    <div data-testid={`rfidText-${chargePoint.id}`}>
                        <Text code="lblRFIDLock" />
                    </div>
                }
                button={
                    <Toggle
                        toggleFn={() =>
                            toggleOcppConfigValue(KEY_FREE_CHARGING, isNfcLockOn ? NfcLockState.OFF : NfcLockState.ON)
                        }
                        isToggled={isNfcLockOn}
                        disabled={
                            isReadOnly ||
                            onGoingTransactionStates.includes(chargePoint.connectors[0]?.ocppStatus) ||
                            !isOnline ||
                            chargePoint?.ocppEnabled
                        }
                        data-testid={`rfid-${chargePoint.id}`}
                    />
                }
            />
            <SelectRfidTagDialog
                visible={selectRfidDialogIsOpen}
                chargePointId={chargePoint.id}
                connectorId={chargingConnector?.connectorId ?? 1}
                onStartCharging={(rfidTag) => {
                    handleRemoteStart(rfidTag)
                    setSelectRfidDialogIsOpen(false)
                }}
                onHide={() => setSelectRfidDialogIsOpen(false)}
            />
        </RemoteControlWidget>
    )
}
